import type {List} from 'immutable';

import {
  getClassroomIndiePracticeSubjectsQuery,
  getIndiePracticeSubjectsQuery
} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import type {SubjectModelV2} from '@albert-io/models';
import {useStudentHomepageStore} from '../../useStudentHomepageStore';

// Responses write to a module-global store slice, and the tab remounts on every
// classroom switch — drop out-of-order responses so a slow fetch from a previous
// scope can't overwrite the current one.
let latestRequestId = 0;

/**
 * Marks the cached subject queries stale so the next fetch hits the network.
 * Call on enrollment changes; plain tab remounts are served from the cached response.
 */
export function invalidateIndependentPracticeSubjectsCache(classroomId?: string) {
  getIndiePracticeSubjectsQuery().invalidateInterest();

  if (classroomId !== undefined) {
    getClassroomIndiePracticeSubjectsQuery(classroomId).invalidateInterest();
  }
}

export default function useIndependentPracticeSubjects() {
  const areIndependentPracticeSubjectsErrored = useStudentHomepageStore(
    (state) => state.areIndependentPracticeSubjectsErrored
  );
  const areIndependentPracticeSubjectsLoading = useStudentHomepageStore(
    (state) => state.areIndependentPracticeSubjectsLoading
  );
  const independentPracticeSubjects = useStudentHomepageStore(
    (state) => state.independentPracticeSubjects
  );
  const setAreIndependentPracticeSubjectsErrored = useStudentHomepageStore(
    (state) => state.setAreIndependentPracticeSubjectsErrored
  );
  const setAreIndependentPracticeSubjectsLoading = useStudentHomepageStore(
    (state) => state.setAreIndependentPracticeSubjectsLoading
  );
  const setIndependentPracticeSubjects = useStudentHomepageStore(
    (state) => state.setIndependentPracticeSubjects
  );

  function fetchIndependentPracticeSubjects(classroomId: string | undefined) {
    const requestId = ++latestRequestId;

    setAreIndependentPracticeSubjectsLoading(true);
    setAreIndependentPracticeSubjectsErrored(false);

    const query = classroomId
      ? getClassroomIndiePracticeSubjectsQuery(classroomId)
      : getIndiePracticeSubjectsQuery();

    query
      .getResourcePromise()
      .then((result: List<SubjectModelV2>) => {
        if (requestId !== latestRequestId) return;

        const filteredSubjects = result.toArray().filter((subject) => !subject.isAdaptive());
        setIndependentPracticeSubjects(filteredSubjects);
      })
      .catch(() => {
        // Mandark never TTL-refreshes error-status interest entries, so without this a
        // single failure is served as an empty success on every later visit — no request,
        // and the "Try Again" button would silently no-op. Re-dirty the query so the next
        // fetch (tab revisit or retry) actually retries; do it even for a stale response.
        query.invalidateInterest();

        if (requestId !== latestRequestId) return;

        // The tab renders an inline error/retry state off this flag, so no toast here.
        setAreIndependentPracticeSubjectsErrored(true);
        setIndependentPracticeSubjects([]);
      })
      .finally(() => {
        if (requestId !== latestRequestId) return;

        setAreIndependentPracticeSubjectsLoading(false);
      });
  }

  return {
    areIndependentPracticeSubjectsErrored,
    areIndependentPracticeSubjectsLoading,
    fetchIndependentPracticeSubjects,
    independentPracticeSubjects
  };
}
